import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import dayjs from "dayjs";
import { useCrawlers } from "@/context/CrawlerContext";
import StatusBadge from "@/components/StatusBadge";
import PdfViewer from "@/components/PdfViewer";
import CrawlerInfo from "@/components/CrawlerInfo";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  PlayCircle, Play, RefreshCw, UploadCloud, Loader2, FileText,
  ChevronRight, ChevronLeft, Layers, Inbox, Info, X, SearchX, CheckCheck, Check, Minus, FolderInput, ExternalLink,
} from "lucide-react";
import { MobileSidebar, BurgerBar } from "@/components/MobileNav";

const NAME_RE = /^([a-z]{2})_([a-z0-9]+)_(\d{4})_(\d+)_(\d{4})_(\d{4})\.pdf$/;
const fmt = (iso) => (iso ? dayjs(iso).format("DD.MM.YY HH:mm") : "—");

// Neue Status-Icons für die Crawler-Liste (statt farbiger Punkte):
function CrawlerIcon({ st, code }) {
  const cls = "h-4 w-4 shrink-0";
  if (st === "running") return <Loader2 className={`${cls} text-sky-600 animate-spin`} data-testid={`nav-running-${code}`} />;
  if (st === "queued") return <Loader2 className={`${cls} text-amber-500 animate-spin`} data-testid={`nav-icon-${code}`} />;
  if (st === "no_adapter" || st === "skipped") return <X className={`${cls} text-gray-400`} data-testid={`nav-icon-${code}`} />;
  if (st === "empty") return <SearchX className={`${cls} text-orange-500`} data-testid={`nav-icon-${code}`} />;
  if (st === "success") return <CheckCheck className={`${cls} text-green-600`} data-testid={`nav-icon-${code}`} />;
  if (st === "found_no_ocr") return <Check className={`${cls} text-orange-500`} data-testid={`nav-icon-${code}`} />;
  if (st === "error") return <X className={`${cls} text-red-600`} data-testid={`nav-icon-${code}`} />;
  return <Minus className={`${cls} text-gray-300`} data-testid={`nav-icon-${code}`} />;
}

export default function CrawlerDashboard() {
  const { crawlers, busy, runAllTotal, doneCount, progressPct, statusOf, runOne, runAll } = useCrawlers();
  const navigate = useNavigate();
  const [selectedCode, setSelectedCode] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [landFilter, setLandFilter] = useState("alle");
  const [viewMode, setViewMode] = useState("crawler"); // "crawler" | "global"
  const [dateFilter, setDateFilter] = useState(""); // "" = alle Tage
  const [globalDocs, setGlobalDocs] = useState([]);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const inputRef = useRef();

  const loadDocs = useCallback(async (code) => {
    if (!code) return;
    setDocsLoading(true);
    try { const r = await api.get(`/documents`, { params: { crawler: code } }); setDocs(r.data); }
    catch { toast.error("Dokumente konnten nicht geladen werden"); }
    finally { setDocsLoading(false); }
  }, []);
  useEffect(() => { if (selectedCode) loadDocs(selectedCode); }, [selectedCode, loadDocs]);

  // Global: alle gecrawlten Hefte (für den Datumsfilter über alle Crawler).
  const loadGlobalDocs = useCallback(async () => {
    setGlobalLoading(true);
    try { const r = await api.get(`/documents`, { params: { source: "crawl" } }); setGlobalDocs(r.data); }
    catch { toast.error("Dokumente konnten nicht geladen werden"); }
    finally { setGlobalLoading(false); }
  }, []);

  const enterGlobal = useCallback((date) => {
    setViewMode("global"); setDateFilter(date); setSelectedCode(null); setSelectedDocId(null); setNavOpen(false);
    loadGlobalDocs();
  }, [loadGlobalDocs]);
  const showToday = () => enterGlobal(dayjs().format("YYYY-MM-DD"));
  const showAllCrawled = () => enterGlobal("");

  const globalFiltered = useMemo(() => {
    if (!dateFilter) return globalDocs;
    return globalDocs.filter((d) => d.created_at && dayjs(d.created_at).format("YYYY-MM-DD") === dateFilter);
  }, [globalDocs, dateFilter]);

  const selectedCrawler = crawlers.find((c) => c.code === selectedCode);

  const grouped = useMemo(() => {
    const map = {};
    crawlers.forEach((c) => { (map[c.bundesland] = map[c.bundesland] || []).push(c); });
    return Object.entries(map)
      .filter(([land]) => landFilter === "alle" || land === landFilter)
      .sort(([a], [b]) => a.localeCompare(b));
  }, [crawlers, landFilter]);

  const landOptions = useMemo(() => {
    const s = new Set(crawlers.map((c) => c.bundesland).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [crawlers]);

  const retryDoc = async (id) => {
    try {
      await api.post(`/documents/${id}/analyze`);
      toast.success("OCR-Analyse neu gestartet");
      setDocs((ds) => ds.map((x) => (x.id === id ? { ...x, status: "processing" } : x)));
      setGlobalDocs((ds) => ds.map((x) => (x.id === id ? { ...x, status: "processing" } : x)));
    } catch { toast.error("Neustart fehlgeschlagen"); }
  };

  // Direkt zum Heft in der PP springen (öffnet das Dokument im PP-Editor).
  const openInPp = (id) => navigate("/hefte", { state: { openDoc: id, ts: Date.now() } });

  // Heft zurück in die PP schieben: Status → „neu" und direkt in der PP öffnen.
  const moveToPp = async (id) => {
    try {
      await api.put(`/documents/${id}`, { status: "neu" });
      toast.success("In PP verschoben", { description: "Status auf 'Neu' gesetzt" });
      setDocs((ds) => ds.map((x) => (x.id === id ? { ...x, status: "neu" } : x)));
      openInPp(id);
    } catch { toast.error("Verschieben fehlgeschlagen"); }
  };

  const doUpload = async (file) => {
    if (!file) return;
    if (!NAME_RE.test(file.name)) {
      toast.error("Ungültiger Dateiname", { description: "z.B. by_gvbl_2026_01_0114_0150.pdf" });
      return;
    }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Hochgeladen — OCR gestartet", { description: file.name });
      loadDocs(selectedCode);
      if (r.data?.document?.id) setSelectedDocId(r.data.document.id);
    } catch (e) { toast.error("Upload fehlgeschlagen", { description: e?.response?.data?.detail || "" }); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const renderDocItem = (d) => {
    const active = d.id === selectedDocId;
    return (
      <div key={d.id} role="button" tabIndex={0}
        onClick={() => setSelectedDocId(d.id)}
        onKeyDown={(e) => { if (e.key === "Enter") setSelectedDocId(d.id); }}
        data-testid={`doc-item-${d.id}`}
        className={`w-full text-left px-4 py-3 border-b border-gray-100 transition-colors cursor-pointer ${active ? "bg-white border-l-2 border-l-[#0F4C81]" : "border-l-2 border-l-transparent hover:bg-white"}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-xs text-gray-800 truncate">{d.filename}</span>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border shrink-0 ${d.source === "upload" ? "bg-gray-100 text-gray-600 border-gray-300" : "bg-sky-50 text-sky-800 border-sky-300"}`}>{d.source === "upload" ? "Upload" : "Crawl"}</span>
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="text-[11px] text-gray-400">{fmt(d.created_at)}</span>
          <StatusBadge status={d.status} testId={`doc-status-${d.id}`} />
        </div>
        {d.status === "error" && (
          <Button size="sm" variant="outline" className="mt-2 w-full h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
            onClick={(e) => { e.stopPropagation(); retryDoc(d.id); }} data-testid={`doc-retry-${d.id}`} title="OCR erneut versuchen">
            <RefreshCw className="h-3 w-3 mr-1" /> Erneut versuchen
          </Button>
        )}
        {(d.status === "neu" || d.status === "zurueckgestellt") && (
          <Button size="sm" variant="outline" className="mt-2 w-full h-7 text-xs border-[#0F4C81]/40 text-[#0F4C81] hover:bg-[#0F4C81]/5"
            onClick={(e) => { e.stopPropagation(); openInPp(d.id); }} data-testid={`doc-open-pp-${d.id}`} title="Dieses Heft in der PP öffnen">
            <ExternalLink className="h-3 w-3 mr-1" /> In PP öffnen
          </Button>
        )}
        {!["neu", "zurueckgestellt", "processing", "pending", "error"].includes(d.status) && (
          <Button size="sm" variant="outline" className="mt-2 w-full h-7 text-xs border-[#0F4C81]/40 text-[#0F4C81] hover:bg-[#0F4C81]/5"
            onClick={(e) => { e.stopPropagation(); moveToPp(d.id); }} data-testid={`doc-topp-${d.id}`} title="Dieses Heft zurück in die PP verschieben">
            <FolderInput className="h-3 w-3 mr-1" /> In PP verschieben
          </Button>
        )}
      </div>
    );
  };

  const isGlobal = viewMode === "global";
  const selectedDoc = [...docs, ...globalDocs].find((d) => d.id === selectedDocId);
  const middleLabel = isGlobal ? "Gecrawlte Hefte" : (selectedCrawler ? selectedCrawler.blatt_name : "Crawler wählen");

  const crawlerNav = (
    <>
      <div className="shrink-0 p-3 border-b border-gray-100 space-y-2">
        <Button onClick={runAll} className="w-full bg-[#0F4C81] hover:bg-[#0d3f6b] text-white" size="sm" data-testid="dash-run-all" title="Alle verfügbaren Crawler starten">
          <PlayCircle className="h-4 w-4 mr-1.5" /> Alle Crawler starten
        </Button>
        <Select value={landFilter} onValueChange={setLandFilter}>
          <SelectTrigger className="h-8 text-xs" data-testid="dash-filter-land"><SelectValue placeholder="Bundesland" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="alle" data-testid="dash-filter-land-alle">Alle Bundesländer</SelectItem>
            {landOptions.map((l) => (
              <SelectItem key={l} value={l} data-testid={`dash-filter-land-${l}`}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="pt-2 mt-1 border-t border-gray-100 space-y-1.5">
          <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Gecrawlte Hefte (Datum)</div>
          <input type="date" value={dateFilter} onChange={(e) => enterGlobal(e.target.value)} data-testid="dash-date-filter"
            className="w-full h-8 text-xs border border-gray-300 rounded-md px-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/30" />
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" className={`flex-1 h-7 text-xs ${viewMode === "global" && dateFilter === dayjs().format("YYYY-MM-DD") ? "border-[#0F4C81] text-[#0F4C81] bg-[#0F4C81]/5" : ""}`} onClick={showToday} data-testid="dash-date-today" title="Heute gecrawlte Hefte anzeigen">Heute</Button>
            <Button size="sm" variant="outline" className={`flex-1 h-7 text-xs ${viewMode === "global" && !dateFilter ? "border-[#0F4C81] text-[#0F4C81] bg-[#0F4C81]/5" : ""}`} onClick={showAllCrawled} data-testid="dash-date-all" title="Alle gecrawlten Hefte anzeigen">Alle</Button>
          </div>
        </div>
        {busy && runAllTotal > 1 && (
          <div className="flex items-center gap-2 text-[11px] text-gray-500" data-testid="dash-queue-progress">
            <Loader2 className="h-3 w-3 animate-spin text-sky-600 shrink-0" />
            <div className="flex-1">
              <div className="flex justify-between"><span>läuft…</span><span className="font-semibold">{doneCount}/{runAllTotal}</span></div>
              <div className="mt-0.5 h-1 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-[#0F4C81] rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} /></div>
            </div>
          </div>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto py-2">
        {grouped.map(([land, items]) => (
          <div key={land} className="mb-1">
            <div className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">{land}</div>
            {items.map((c) => {
              const st = statusOf(c);
              const active = c.code === selectedCode;
              const noProvider = st === "no_adapter" || st === "skipped";
              return (
                <button key={c.code} onClick={() => { setViewMode("crawler"); setDateFilter(""); setSelectedCode(c.code); setSelectedDocId(null); setNavOpen(false); }}
                  data-testid={`nav-crawler-${c.code}`}
                  className={`w-full text-left px-4 py-2 flex items-center gap-2 group transition-colors ${active ? "bg-[#0F4C81]/10 border-l-2 border-[#0F4C81]" : "border-l-2 border-transparent hover:bg-gray-50"}`}>
                  <CrawlerIcon st={st} code={c.code} />
                  <span className="flex-1 min-w-0">
                    <span className={`block text-sm font-medium truncate ${noProvider ? "line-through text-gray-400" : active ? "text-[#0F4C81]" : "text-gray-800"}`}>{c.blatt}</span>
                    <span className="block text-[11px] text-gray-400 truncate">{c.blatt_name}</span>
                  </span>
                  <ChevronRight className={`h-4 w-4 shrink-0 ${active ? "text-[#0F4C81]" : "text-gray-300 group-hover:text-gray-400"}`} />
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="h-full flex">
      {/* LEFT: crawler navigation — desktop inline */}
      <nav className="hidden xl:flex w-64 shrink-0 border-r border-gray-200 bg-white flex-col min-h-0" data-testid="crawler-nav">
        {crawlerNav}
      </nav>
      {/* LEFT: mobile burger sheet */}
      <MobileSidebar open={navOpen} onOpenChange={setNavOpen} title="Crawler">
        <div className="flex flex-col min-h-0 h-full bg-white">{crawlerNav}</div>
      </MobileSidebar>

      {/* MIDDLE: documents — hidden on mobile when a doc is open */}
      <div className={`${selectedDocId ? "hidden xl:flex" : "flex"} w-full xl:w-96 shrink-0 border-r border-gray-200 bg-gray-50 flex-col min-h-0`} data-testid="document-panel">
        <BurgerBar onOpen={() => setNavOpen(true)} label={middleLabel} />
        {(!isGlobal && !selectedCrawler) ? (
          <div className="flex-1 flex items-center justify-center p-6 text-center">
            <div className="text-gray-400">
              <Layers className="h-8 w-8 mx-auto" />
              <p className="mt-2 text-sm">Wählen Sie ein Blatt<br />oder nutzen Sie den Datumsfilter.</p>
            </div>
          </div>
        ) : isGlobal ? (
          <>
            <div className="shrink-0 p-4 border-b border-gray-200 bg-white">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="font-heading font-bold text-gray-900 text-sm truncate">Gecrawlte Hefte</h2>
                  <p className="text-[11px] text-gray-400" data-testid="global-date-label">
                    {dateFilter ? `am ${dayjs(dateFilter).format("DD.MM.YYYY")}` : "alle Tage"} · {globalFiltered.length} Hefte
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={loadGlobalDocs} data-testid="global-refresh" title="Liste aktualisieren"><RefreshCw className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {globalLoading ? (
                <div className="py-16 flex justify-center text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : globalFiltered.length === 0 ? (
                <div className="py-16 text-center text-gray-400">
                  <Inbox className="h-7 w-7 mx-auto" />
                  <p className="mt-2 text-sm">Keine gecrawlten Hefte {dateFilter ? "an diesem Tag" : ""}.</p>
                </div>
              ) : (
                globalFiltered.map(renderDocItem)
              )}
            </div>
          </>
        ) : (
          <>
            <div className="shrink-0 p-4 border-b border-gray-200 bg-white">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="font-heading font-bold text-gray-900 text-sm truncate">{selectedCrawler.blatt_name}</h2>
                  <p className="text-[11px] text-gray-400 font-mono">{selectedCrawler.code} · {selectedCrawler.bundesland}</p>
                </div>
                <StatusBadge status={statusOf(selectedCrawler)} testId="panel-crawler-status" />
              </div>
              <div className="mt-2 text-[11px] text-gray-500">
                <span className="text-gray-400">Letzter Lauf:</span> {fmt(selectedCrawler.last_run_at)}
                {typeof selectedCrawler.last_new_count === "number" && <span className="ml-2 text-gray-400">· zuletzt neu: <b className="text-gray-600">{selectedCrawler.last_new_count}</b></span>}
              </div>
              {selectedCrawler.last_error && statusOf(selectedCrawler) === "error" && (
                <div className="mt-1 text-[11px] text-red-700 break-words" data-testid="panel-crawler-error">{selectedCrawler.last_error}</div>
              )}
              <div className="mt-3 flex items-center gap-2">
                {(() => {
                  const st = statusOf(selectedCrawler);
                  const running = st === "running";
                  const queued = st === "queued";
                  const blocked = running || queued;
                  return (
                    <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0d3f6b] text-white flex-1 disabled:opacity-70 disabled:cursor-not-allowed" disabled={blocked} onClick={() => runOne(selectedCrawler.code)} data-testid="panel-start-crawler" title="Diesen Crawler jetzt starten">
                      {blocked ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                      {running ? "Läuft…" : queued ? "In Warteschlange…" : "Crawler starten"}
                    </Button>
                  );
                })()}
                <input ref={inputRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} data-testid="upload-input" />
                <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="panel-upload" title="PDF hochladen">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                </Button>
                <Button size="sm" variant="outline" onClick={() => loadDocs(selectedCode)} data-testid="panel-refresh" title="Liste aktualisieren"><RefreshCw className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => setInfoOpen(true)} title="Crawler-Regeln" data-testid="panel-info"><Info className="h-4 w-4" /></Button>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
              {docsLoading ? (
                <div className="py-16 flex justify-center text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : docs.length === 0 ? (
                <div className="py-16 text-center text-gray-400">
                  <Inbox className="h-7 w-7 mx-auto" />
                  <p className="mt-2 text-sm">Keine Dokumente.</p>
                  <p className="text-xs">Crawler starten oder PDF hochladen.</p>
                </div>
              ) : (
                docs.map(renderDocItem)
              )}
            </div>
          </>
        )}
      </div>

      {/* RIGHT: PDF viewer — full screen on mobile when a doc is selected */}
      <div className={`${selectedDocId ? "flex" : "hidden xl:flex"} flex-1 min-w-0 h-full bg-white flex-col min-h-0`}>
        <div className="xl:hidden shrink-0 flex items-center gap-2 px-3 py-2 border-b border-gray-200 bg-white">
          <Button variant="outline" size="sm" className="h-9 px-2.5 shrink-0" onClick={() => setSelectedDocId(null)} data-testid="mobile-doc-back" title="Zurück zur Liste">
            <ChevronLeft className="h-4 w-4 mr-1" /> Liste
          </Button>
          <span className="text-xs font-mono text-gray-700 truncate flex-1 min-w-0">{selectedDoc?.filename}</span>
        </div>
        <div className="flex-1 min-h-0">
          {selectedDocId ? (
            <PdfViewer docId={selectedDocId} />
          ) : (
            <div className="h-full flex items-center justify-center text-center p-8">
              <div className="text-gray-300">
                <FileText className="h-12 w-12 mx-auto" />
                <p className="mt-3 text-sm text-gray-500">Wählen Sie ein Dokument, um das PDF anzuzeigen.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <CrawlerInfo code={selectedCode} open={infoOpen} onOpenChange={setInfoOpen} />
    </div>
  );
}
